import type { RowData } from "@tanstack/react-table";
import type {
    DataGridCell,
    DataGridEditor,
    DataGridResolvedColumnMeta,
} from "../columns/types";
import type { EditContext } from "../editing/editActions";
import type { DataGridActiveEditor } from "../types";

export interface CellEditing<TData extends RowData> {
    editor: DataGridEditor;
    seed: string | null;
    context: EditContext<TData>;
}

export interface CellEditState<TData extends RowData> {
    isHighlighted: boolean;
    invalidMessage: string | undefined;
    editing: CellEditing<TData> | null;
}

/** `editing.validate`'s message for an editable cell outside group rows. */
export const getInvalidMessage = <TData extends RowData>({
    cell,
    meta,
    editContext,
}: {
    cell: DataGridCell<TData>;
    meta: DataGridResolvedColumnMeta;
    editContext: EditContext<TData> | null;
}) => {
    const validate = editContext?.instance.optionsRef.current.editing?.validate;
    if (!validate || !meta.editor || cell.row.getIsGrouped()) {
        return undefined;
    }
    return validate({ row: cell.row.original, columnId: cell.column.id });
};

/** A cell's highlight, validation, and editor props, from its row's state. */
export const getCellEditState = <TData extends RowData>({
    cell,
    meta,
    highlightKey,
    editor,
    editContext,
}: {
    cell: DataGridCell<TData>;
    meta: DataGridResolvedColumnMeta;
    highlightKey: string;
    editor: DataGridActiveEditor | null;
    editContext: EditContext<TData> | null;
}): CellEditState<TData> => {
    const columnId = cell.column.id;
    const isEditing =
        editor !== null &&
        editor.columnId === columnId &&
        editContext !== null &&
        meta.editor !== undefined;
    return {
        isHighlighted:
            highlightKey !== "" && highlightKey.split(" ").includes(columnId),
        invalidMessage: getInvalidMessage({ cell, meta, editContext }),
        editing:
            isEditing && meta.editor
                ? {
                      editor: meta.editor,
                      seed: editor.seed,
                      context: editContext,
                  }
                : null,
    };
};
